// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Pages
import { FileInputMateDrive, TabPanel } from '../../../FlowConfigurationInputs';
import { CredentialPicker } from '../../../FlowConfigurationInputs/CredentialPicker/CredentialPicker';
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

// Import Material UI
import { Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import CancelIcon from '@material-ui/icons/HighlightOff';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated, addDifference, removeDifference, checkTheDifference } from '../../../../helpers/helper';

/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @Param       variableRenbder - variable render
 * @Returns     Component
 * @Author      ibrahim AKIN
 */
function SendMail({ action, variables, setOpenVariable, automId }) {

    const { t } = useTranslation();
    const [attachments, setAttachments] = useState([{ tab: 'browse' }])
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    const label = 'CONTENT.CONFIGURATION-FORM.EMAIL.SEND-MAIL.';

    useEffect(() => {
        if (!action.fields.attachments) {
            let newValue = [{ tab: 'browse' }]
            action.fields.attachments = { value: newValue, type: 'ATTACHMENTS' };
            setAttachments(newValue);
        } else {
            setAttachments(action.fields.attachments.value);
        }
    }, [action]);

    const handleChange = (newValue, item) => {
        item.tab = newValue;
        if (newValue === 'browse') {
            delete item.variable;
        } else if (newValue === 'variable') {
            delete item.browse;
        }
        setAttachments([...attachments]);
    };

    const handleAdd = () => {
        const newValue = [...attachments, { tab: 'browse' }];
        action.fields.attachments = { value: newValue, type: 'ATTACHMENTS' };
        changeManagementForAttachment();
        setAttachments(newValue);
    }
    const handleDelete = (itemI) => {

        attachments.splice(itemI, 1);
        action.fields.attachments = { value: attachments, type: 'ATTACHMENTS' };
        changeManagementForAttachment();
        setAttachments([...attachments]);
    }

    const changeManagementForAttachment = () => {

        let field = initialActionsData[action.id]?.fields.attachments
        const cfTemp = JSON.parse(JSON.stringify(changedFieldsState));
        const oldValue = !field ? undefined : field.value;
        if (!oldValue) {
            addDifference(cfTemp, action, 'attachments', setChangeFields, setSaveAvailable);
        } else {
            const arr1 = action.fields.attachments.value;
            if (checkTheDifference(arr1, oldValue, ['variable', 'browse'])) {
                addDifference(cfTemp, action, 'attachments', setChangeFields, setSaveAvailable)
            } else {
                removeDifference(cfTemp, action, 'attachments', setChangeFields, setSaveAvailable)
            }
        }
    }

    return (
        <>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'HOST')} is_required />
                <TextInput action={action} inputName="host" variables={variables} setOpenVariable={setOpenVariable}
                    isRequired fieldType="string" displayName={t(label + 'HOST')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'PORT')} is_required />
                <TextInput action={action} inputName="port" variables={variables} setOpenVariable={setOpenVariable}
                    isRequired fieldType="number" displayName={t(label + 'PORT')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'USERNAME')} is_required />
                <CredentialPicker action={action} inputName="userName" variables={variables}
                    setOpenVariable={setOpenVariable} isRequired hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'PASSWORD')} is_required />
                <CredentialPicker action={action} inputName="password" variables={variables}
                    setOpenVariable={setOpenVariable} isRequired isPassword={true} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'FROM')} is_required />
                <TextInput action={action} inputName="from" variables={variables} setOpenVariable={setOpenVariable}
                    isRequired fieldType="string" displayName={t(label + 'FROM')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'SENDER-NAME')} is_required />
                <TextInput action={action} inputName="senderName" variables={variables}
                    setOpenVariable={setOpenVariable} isRequired fieldType="string" displayName={t(label + 'SENDER-NAME')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'TO')} is_required />
                <TextInput action={action} inputName="to" variables={variables} setOpenVariable={setOpenVariable}
                    isRequired fieldType="string" displayName={t(label + 'TO')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'CC')} />
                <TextInput action={action} inputName="cc" variables={variables} setOpenVariable={setOpenVariable} fieldType="string" displayName={t(label + 'CC')} />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'SUBJECT')} is_required />
                <TextInput action={action} inputName="subject" variables={variables} setOpenVariable={setOpenVariable}
                    isRequired fieldType="string" displayName={t(label + 'SUBJECT')} hideRequired />
            </div>
            <div className="conf-field-container">
                <div className='items-center-between'>
                    <FieldLabel name={t(label + 'ATTACHMENTS')} />
                    <Button onClick={handleAdd}
                        style={{ border: "none", color: "#8F94A6", fontWeight: 700, textTransform: "none" }}>
                        <AddIcon style={{ paddingRight: 5 }} />
                    </Button>
                </div>
                <div className="attachment-container">
                    {
                        attachments.length < 1 ? <div className="conf-field-desc">{t(label + 'ATTACHMENT-NOT-FOUND-MESSSAGE')}</div> :
                            attachments.map((item, itemI) => {
                                return (
                                    <div key={itemI} style={{ marginBottom: 10 }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className="condition-tabs form-element">
                                                <button className={`f-s-11 col-4 ${item.tab === 'browse' ? 'condition-active' : ''}`}
                                                    value="browse" onClick={() => handleChange("browse", item)}>
                                                    Browse
                                                </button>
                                                <button className={`f-s-11 col-4 ${item.tab === 'variable' ? 'condition-active' : ''}`}
                                                    value="variable" onClick={() => handleChange("variable", item)}>
                                                    {t(label + 'CUSTOM-STRING')}
                                                </button>
                                                <CancelIcon onClick={() => {
                                                    handleDelete(itemI);
                                                }} style={{ paddingRight: 5, float: 'right', cursor: 'pointer' }} />
                                            </div>
                                        </div>
                                        <TabPanel value={item.tab} index={'browse'}>
                                            <FileInputMateDrive action={item} index={itemI} inputName="browse" isSize onChangeFile={changeManagementForAttachment} autom_id={automId} />
                                        </TabPanel>
                                        <TabPanel value={item.tab} index={'variable'}>
                                            <TextInput action={item} inputName="variable" variables={variables}
                                                setOpenVariable={setOpenVariable}
                                                setField={changeManagementForAttachment}
                                                mateDrivePath="filePath"
                                                autom_id={automId} />
                                        </TabPanel>
                                    </div>
                                )
                            })
                    }
                </div>

            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'BODY')} />
                <TextInput showOpenModal={true} outLabel={label} type="html" action={action} inputName="body" variables={variables} setOpenVariable={setOpenVariable}
                    multiline fieldType="string" displayName={t(label + 'BODY')} />
            </div>
        </>
    );
}

export default SendMail;
